import {
  Alert, Box, Card, CardContent, Chip, Divider, Grid, Link, Stack, Tooltip, Typography
} from '@mui/material';
import {
  Button, Show, TopToolbar, useNotify, useRecordContext, useRefresh, type RaRecord
} from 'react-admin';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Button as MuiButton, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import { BackLink } from './back';
import { OrderEditor } from './orderEdit';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { callAction } from './dataProvider';
import { FileImage } from './image';

interface Address {
  line1: string | null; line2: string | null; postcode: string | null;
  city: string | null; country: string | null; company: string | null;
  name: string | null; firstName: string | null; lastName: string | null;
  phone: string | null; email: string | null;
  ico: string | null; dic: string | null; icDph: string | null;
}

/**
 * Everything below `fulfilled` is optional because react-admin paints this
 * screen twice: once with the record it already holds from the list, and again
 * when the detail request returns. The list record has none of these fields,
 * so treating them as guaranteed crashes the first render.
 */
type Order = RaRecord & {
  customer: string; customerEmail?: string | null; organizationName?: string | null;
  customerId?: number | null; organizationId?: number | null;
  ordered?: string | null; approved?: string | null; fulfilled: boolean;
  invoice?: string | null; invoiceSentAt?: string | null; note?: string | null; viaQzila?: boolean;
  invoiceAddress?: Address | null; deliveryAddress?: Address | null;
  lines?: {
    id: number; code: string | null; name: string | null; quantity: number;
    unitPrice?: number | null; fileIdentifier?: string | null; productId?: number | null;
    printers?: string[]; description?: string | null; computedUnitPrice?: number | null;
  }[];
  empties?: {
    id: number; code: string | null; name: string | null; quantity: number;
    note: string | null; fileIdentifier?: string | null;
  }[];
  total?: number | null; totalWithVat?: number | null; computedTotal?: number | null;
  approvedBy?: string | null; deliveryConfirmedAt?: string | null;
  groupName?: string | null;
  groupOriginalDiscount?: number | null; groupRenewalDiscount?: number | null;
  paymentId?: string | null; paymentStatus?: string | null;
  deliveryMethod?: string | null; paymentMethod?: string | null;
  deliveryMethodName?: string | null; paymentMethodName?: string | null;
  invalidated?: boolean;
  auditTrail?: { id: number; action: string; actorEmail: string | null; created: string }[];
  created?: string | null;
};

const EUR = new Intl.NumberFormat('sk-SK', { style: 'currency', currency: 'EUR' });

function AddressBlock({ title, address }: { title: string; address: Address | null }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">{title}</Typography>
        {address == null ? (
          <Typography variant="body2" color="text.secondary">Neuvedené</Typography>
        ) : (
          /* Labelled rows rather than a run-together block: an admin reading
             this is usually checking one specific field - the IČO, the phone -
             and scanning for it in a paragraph is slower than it needs to be.
             Empty fields are left out rather than shown blank. */
          <Stack sx={{ mt: 1 }} spacing={0.25}>
            <AddressRow label="Meno" value={address.name} />
            <AddressRow label="Spoločnosť" value={address.company} />
            <AddressRow label="E-mail" value={address.email} />
            <AddressRow label="Telefón" value={address.phone} />
            <AddressRow label="Adresa" value={address.line1} />
            <AddressRow label="Adresa 2" value={address.line2} />
            <AddressRow label="Mesto" value={address.city} />
            <AddressRow label="PSČ" value={address.postcode} />
            <AddressRow label="Krajina" value={address.country?.toUpperCase() ?? null} />
            <AddressRow label="IČO" value={address.ico} />
            <AddressRow label="DIČ" value={address.dic} />
            <AddressRow label="IČ DPH" value={address.icDph} />
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function AddressRow({ label, value }: { label: string; value: string | null }) {
  if (value == null || value === '') return null;

  return (
    <Stack direction="row" spacing={1}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 88 }}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Stack>
  );
}

/**
 * How it was meant to arrive and how it was meant to be paid for.
 *
 * Both columns hold two vocabularies - an integer up to June 2022, a qzila
 * UUID since - so the readable name is resolved server-side. An unmapped value
 * shows the raw code rather than nothing, because a method that appears here
 * unrecognised is a mapping that needs updating, not a blank to ignore.
 */
function DeliveryAndPayment() {
  const record = useRecordContext<Order>();
  if (record == null) return null;

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="overline" color="text.secondary">Doprava a platba</Typography>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">Spôsob dopravy</Typography>
            <Typography variant="body2">
              {record.deliveryMethodName ?? record.deliveryMethod ?? 'Neuvedené'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">Spôsob platby</Typography>
            <Typography variant="body2">
              {record.paymentMethodName ?? record.paymentMethod ?? 'Neuvedené'}
            </Typography>
          </Grid>
        </Grid>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
          Poznámka k objednávke
        </Typography>
        <Typography variant="body2" color={record.note == null ? 'text.secondary' : 'text.primary'}>
          {record.note == null || record.note === '' ? 'žiadna poznámka' : record.note}
        </Typography>
      </CardContent>
    </Card>
  );
}

function LineTable({ title, rows, empty }: {
  title: string;
  // Optional on purpose. react-admin renders Show with the record it already
  // has from the list, then re-renders when the detail arrives - and the list
  // record carries no lines or empties, so this is undefined on first paint.
  rows: {
    id: number; code: string | null; name: string | null; quantity: number;
    note?: string | null; unitPrice?: number | null; fileIdentifier?: string | null;
    productId?: number | null; printers?: string[]; description?: string | null;
    computedUnitPrice?: number | null;
  }[] | undefined;
  empty: string;
}) {
  const items = rows ?? [];

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="overline" color="text.secondary">{title}</Typography>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{empty}</Typography>
        ) : (
          <Stack divider={<Divider flexItem />} sx={{ mt: 1 }}>
            {items.map(row => (
              <Stack key={row.id} direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.75 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                  {/* A fixed box either way, so lines with and without a
                      picture keep the same left edge. */}
                  <Tooltip title={row.description ?? ''} placement="right" arrow>
                    <Box sx={{ width: 40, height: 40, flexShrink: 0 }}>
                      <FileImage id={row.fileIdentifier} size={40} alt={row.name ?? ''} />
                    </Box>
                  </Tooltip>
                  <Stack sx={{ minWidth: 0 }}>
                    {/* The line keeps its own copy of the name, but the product
                        is usually what you want next - stock, price, or what
                        else fits the same printer. */}
                    <LineTitle
                      id={row.id}
                      name={row.name}
                      code={row.code}
                      productId={row.productId}
                    />
                    {row.note != null && (
                      <Typography variant="caption" color="text.secondary">{row.note}</Typography>
                    )}
                    <PrinterList printers={row.printers ?? []} />
                  </Stack>
                </Stack>
                <Stack alignItems="flex-end">
                  <Typography variant="body2">&times;&nbsp;{row.quantity}</Typography>
                  <LinePrice
                    unitPrice={row.unitPrice}
                    computed={row.computedUnitPrice}
                    quantity={row.quantity}
                  />
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * What the order came to.
 *
 * Recorded from the moment the shop started keeping it. Orders placed before
 * that carry no total, and none is invented for them: a figure computed from
 * today's prices would read like an invoice while being a guess, since group
 * discounts and sales have all moved since.
 */
function OrderTotal() {
  const record = useRecordContext<Order>();
  if (record == null) return null;

  if (record.total == null) {
    if (record.computedTotal == null) {
      return (
        <Alert severity="info" variant="outlined">
          Pri tejto objednávke nie je uložená suma a nedá sa ani prepočítať — niektoré položky už
          nemajú cenník. Čo zákazník zaplatil, nájdete v službe qzila alebo v Stripe.
        </Alert>
      );
    }

    return (
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
            <Typography variant="overline" color="text.secondary">Prepočítaná suma bez DPH</Typography>
            <Typography variant="h6" sx={{ fontStyle: 'italic' }}>
              ≈ {EUR.format(record.computedTotal)}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Objednávka nemá uloženú sumu — je staršia než jej evidencia. Toto je prepočet podľa
            dnešného cenníka a cenovej skupiny zákazníka rovnakými pravidlami, aké používa e-shop.
            Skutočne účtovaná suma môže byť iná; nájdete ju v Stripe alebo v qzila.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
          <Typography variant="overline" color="text.secondary">Spolu bez DPH</Typography>
          <Typography variant="body1">{EUR.format(record.total)}</Typography>
        </Stack>
        {record.totalWithVat != null && (
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mt: 1 }}>
            <Typography variant="overline" color="text.secondary">Spolu s DPH</Typography>
            <Typography variant="h6">{EUR.format(record.totalWithVat)}</Typography>
          </Stack>
        )}
        <Typography variant="caption" color="text.secondary">
          Súčet položiek objednávky. Doprava a poplatky brány sa tu nezapočítavajú.
        </Typography>
      </CardContent>
    </Card>
  );
}

/**
 * The price group the customer is in.
 *
 * On this screen because it explains the prices below it: a line at a number
 * that does not match the catalogue is a Díler discount, not an error. Shown
 * even when there is no group, since "pays list price" is equally an answer.
 */
function CustomerGroup() {
  const record = useRecordContext<Order>();
  if (record == null) return null;

  const percentages = [record.groupOriginalDiscount, record.groupRenewalDiscount];
  const hasPercentages = percentages.every(value => value != null);

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="caption" color="text.secondary">Cenová skupina:</Typography>
      {record.groupName == null
        ? <Chip size="small" variant="outlined" label="Bez skupiny — štandardné ceny" />
        : (
          <Tooltip
            title={hasPercentages
              ? `Zľava na originál ${record.groupOriginalDiscount} %, na renováciu ${record.groupRenewalDiscount} % — platí len na náplne`
              : 'Skupina určuje ceny, ktoré tento zákazník vidí'}
          >
            <Chip size="small" color="primary" label={record.groupName} />
          </Tooltip>
        )}
    </Stack>
  );
}

/**
 * The payment, as far as this database knows it.
 *
 * A card order carries a Stripe reference and its status. The checkout runs in
 * qzila's Stripe account and this service holds no key for it, so the id links
 * out rather than pretending to show the card and the fee.
 *
 * An order with no reference is usually not unpaid - it is cash on delivery or
 * an invoice settled by transfer, and nothing tells this system when the money
 * arrived. An administrator can record that, and undo it: the usual reason to
 * mark an order paid by mistake is two people on the same screen. Both are
 * logged, and a Stripe payment cannot be overridden here at all.
 */
function PaymentCard() {
  const record = useRecordContext<Order>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);

  if (record == null) return null;

  const viaStripe = record.paymentId != null && record.paymentStatus === 'succeeded';
  const markedByHand = record.paymentStatus === 'manual';

  const setPaid = async (paid: boolean) => {
    setBusy(true);
    try {
      await callAction(`orders/${record.id}/paid`, 'POST', { paid });
      notify(paid ? 'Objednávka označená ako zaplatená.' : 'Označenie platby zrušené.', { type: 'success' });
      refresh();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Stav platby sa nepodarilo zmeniť.', { type: 'warning' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Stack sx={{ minWidth: 0 }}>
            <Typography variant="overline" color="text.secondary">Platba</Typography>

            {viaStripe ? (
              <>
                <Tooltip title="Otvoriť platbu v Stripe">
                  <Link
                    href={`https://dashboard.stripe.com/payments/${record.paymentId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {record.paymentId}
                  </Link>
                </Tooltip>
                <Typography variant="caption" color="text.secondary">
                  Stripe PaymentIntent — kartu, poplatok a výplatu nájdete v Stripe.
                </Typography>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {markedByHand
                  ? 'Zaplatenie zaznamenal administrátor — nejde o platbu kartou cez Stripe.'
                  : `Bez platby kartou. ${describeMethod(record.paymentMethod)}`}
              </Typography>
            )}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            {viaStripe && <Chip size="small" color="success" label="Zaplatená kartou" />}
            {markedByHand && <Chip size="small" color="success" variant="outlined" label="Zaplatená (ručne)" />}
            {!viaStripe && !markedByHand && (
              <Chip size="small" color="warning" variant="outlined" label="Nezaplatená" />
            )}

            {!viaStripe && (
              <Button
                label={markedByHand ? 'Zrušiť označenie' : 'Označiť ako zaplatenú'}
                disabled={busy}
                onClick={() => void setPaid(!markedByHand)}
              />
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

/**
 * The stored method is a qzila identifier on everything since June 2022, so it
 * is only worth showing when it is actually readable.
 */
function describeMethod(method: string | null | undefined): string {
  if (method == null || method === '') return 'Spôsob platby nie je zaznamenaný.';
  if (/^[0-9a-f-]{20,}$/i.test(method)) return 'Spôsob platby eviduje qzila.';
  return `Spôsob platby: ${method}.`;
}

/**
 * A line's product, named once.
 *
 * Most products in this catalogue have the code as their heading, so printing
 * both produced "HP F6V25AE 652HP F6V25AE 652". The code is only worth a
 * second line when it actually says something the name does not.
 */
function LineTitle({ id, name, code, productId }: {
  id: number;
  name: string | null;
  code: string | null;
  productId: number | null | undefined;
}) {
  const title = name ?? code ?? `#${id}`;
  const subtitle = code != null && code !== name ? code : null;

  return (
    <>
      {productId == null ? (
        <Typography variant="body2" noWrap>{title}</Typography>
      ) : (
        <Link
          component={RouterLink}
          to={`/products/${productId}`}
          variant="body2"
          underline="hover"
          noWrap
        >
          {title}
        </Link>
      )}
      {subtitle != null && (
        <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
      )}
    </>
  );
}

/**
 * Which printers an item fits.
 *
 * A consumable's code says nothing on its own - "CF259X" only becomes
 * recognisable next to "LaserJet Pro M404". Capped at three with a count,
 * because a popular toner fits dozens and the list would swallow the order.
 */
function PrinterList({ printers }: { printers: readonly string[] }) {
  if (printers.length === 0) return null;

  const shown = printers.slice(0, 3);
  const rest = printers.length - shown.length;

  return (
    <Tooltip title={printers.join(', ')} placement="bottom-start">
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: 420
        }}
      >
        Pre: {shown.join(', ')}{rest > 0 ? ` +${rest} ďalších` : ''}
      </Typography>
    </Tooltip>
  );
}

/**
 * Every logged change to this order.
 *
 * Empty means nobody has altered it since it arrived - the normal and
 * desirable state - so the card only appears once there is something to say.
 */
function OrderAudit() {
  const record = useRecordContext<Order>();
  const trail = record?.auditTrail ?? [];
  if (trail.length === 0) return null;

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="overline" color="text.secondary">Zásahy administrátora</Typography>
        <Stack divider={<Divider flexItem />} sx={{ mt: 1 }}>
          {trail.map(entry => (
            <Stack key={entry.id} direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.75 }}>
              <Typography variant="body2">{describeAction(entry.action)}</Typography>
              <Stack alignItems="flex-end">
                <Typography variant="caption" color="text.secondary">{formatMoment(entry.created)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {entry.actorEmail ?? 'neznámy používateľ'}
                </Typography>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

function describeAction(action: string): string {
  if (action === 'marked_paid') return 'Označená ako zaplatená';
  if (action === 'payment_reverted') return 'Označenie platby zrušené';
  return action;
}

/**
 * What happened to this order, and when.
 *
 * Reconstructed from the timestamps the order carries rather than an event
 * log, because there is no event log - so a step with no timestamp is shown as
 * not having happened rather than guessed at. Payment is the one exception: it
 * has a status and a Stripe reference but no time of its own, and saying so is
 * more useful than leaving the row out.
 */
function OrderHistory() {
  const record = useRecordContext<Order>();
  if (record == null) return null;

  const steps: { label: string; at: string | null | undefined; detail?: string | null }[] = [
    { label: 'Vytvorená', at: record.created },
    { label: 'Objednaná', at: record.ordered },
    {
      label: 'Zaplatená',
      at: null,
      detail: record.paymentStatus === 'succeeded' ? 'potvrdené Stripe' : record.paymentStatus ?? null
    },
    { label: 'Faktúra odoslaná', at: record.invoiceSentAt },
    { label: 'Doručenie potvrdené', at: record.deliveryConfirmedAt },
    {
      label: 'Vybavená',
      at: record.approved,
      detail: record.approvedBy == null ? null : `označil ${record.approvedBy}`
    }
  ];

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="overline" color="text.secondary">História objednávky</Typography>
        <Stack divider={<Divider flexItem />} sx={{ mt: 1 }}>
          {steps.map(step => {
            const happened = step.at != null || step.detail != null;

            return (
              <Stack
                key={step.label}
                direction="row"
                justifyContent="space-between"
                spacing={2}
                sx={{ py: 0.75, opacity: happened ? 1 : 0.5 }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: happened ? 'success.main' : 'action.disabled',
                      flexShrink: 0
                    }}
                  />
                  <Typography variant="body2">{step.label}</Typography>
                </Stack>
                <Stack alignItems="flex-end">
                  <Typography variant="body2" color={happened ? 'text.primary' : 'text.secondary'}>
                    {step.at != null ? formatMoment(step.at) : happened ? '—' : 'zatiaľ nie'}
                  </Typography>
                  {step.detail != null && (
                    <Typography variant="caption" color="text.secondary">{step.detail}</Typography>
                  )}
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
}

function formatMoment(value: string): string {
  return new Date(value).toLocaleString('sk-SK', {
    day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
}

/**
 * A line's money, and whether it is a record or a reconstruction.
 *
 * A recorded price is what the customer was charged. A computed one is what
 * they would pay today, worked out from the current price list and their
 * group with the same rules the storefront uses - useful, but not the same
 * claim, so it is marked and styled differently rather than shown as fact.
 */
function LinePrice({ unitPrice, computed, quantity }: {
  unitPrice: number | null | undefined;
  computed: number | null | undefined;
  quantity: number;
}) {
  if (unitPrice != null) {
    return (
      <Typography variant="caption" color="text.secondary">
        {EUR.format(unitPrice)} / ks · <strong>{EUR.format(unitPrice * quantity)}</strong>
      </Typography>
    );
  }

  if (computed == null) {
    return <Typography variant="caption" color="text.secondary">cena neevidovaná</Typography>;
  }

  return (
    <Tooltip title="Prepočítané podľa dnešného cenníka a cenovej skupiny zákazníka. Objednávka sumu neeviduje — takto by to stálo teraz.">
      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        ≈ {EUR.format(computed)} / ks · {EUR.format(computed * quantity)}
      </Typography>
    </Tooltip>
  );
}

function OrderBody() {
  const record = useRecordContext<Order>();
  if (record == null) return null;

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <InvalidatedBanner />
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="h6">Objednávka č. {record.id}</Typography>
        {record.fulfilled
          ? <Chip size="small" color="success" label="Vybavená" />
          : <Chip size="small" color="warning" variant="outlined" label="Otvorená" />}
        {record.viaQzila && <Chip size="small" variant="outlined" label="qzila checkout" />}
      </Stack>

      <CustomerGroup />

      <Typography variant="body2">
        {/* The customer and their organisation are usually the next thing you
            want - what else they have ordered, which group they are in - so
            they are links wherever there is still an account to open. */}
        {record.customerId == null ? (
          record.customer
        ) : (
          <Link component={RouterLink} to={`/users/${record.customerId}/show`} underline="hover">
            {record.customer}
          </Link>
        )}
        {record.customerEmail != null && <> · {record.customerEmail}</>}
        {record.organizationName != null && (
          <>
            {' · '}
            {record.organizationId == null ? (
              record.organizationName
            ) : (
              <Link
                component={RouterLink}
                to={`/organizations/${record.organizationId}/show`}
                underline="hover"
              >
                {record.organizationName}
              </Link>
            )}
          </>
        )}
      </Typography>

      {record.note != null && <Alert severity="info">{record.note}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}><AddressBlock title="Fakturačná adresa" address={record.invoiceAddress ?? null} /></Grid>
        <Grid item xs={12} md={6}><AddressBlock title="Dodacia adresa" address={record.deliveryAddress ?? null} /></Grid>
      </Grid>

      <LineTable title="Položky" rows={record.lines} empty="Objednávka neobsahuje položky." />
      <LineTable title="Vrátené prázdne kusy" rows={record.empties} empty="Žiadne vrátené prázdne kusy." />

      <DeliveryAndPayment />
      <OrderTotal />
      <PaymentCard />
      <OrderHistory />
      <OrderAudit />
      <OrderEditor />
      <OrderEditor />
    </Stack>
  );
}

/**
 * Taking an order out of circulation, and putting it back.
 *
 * Confirmed rather than immediate, because it removes the order from every
 * list at once - and reversible from this same screen, since an order
 * cancelled by mistake is a customer wondering where their toner is.
 */
function InvalidateButton() {
  const record = useRecordContext<Order>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);

  if (record == null) return null;

  const invalidated = record.invalidated === true;

  const apply = async () => {
    setBusy(true);
    try {
      await callAction(`orders/${record.id}/invalidate`, 'POST', { invalidated: !invalidated });
      notify(invalidated ? 'Objednávka obnovená.' : 'Objednávka zneplatnená.', { type: 'success' });
      setAsking(false);
      refresh();
    } catch {
      notify('Zmena sa nepodarila.', { type: 'warning' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        label={invalidated ? 'Obnoviť objednávku' : 'Zneplatniť objednávku'}
        onClick={() => setAsking(true)}
      >
        <BlockIcon />
      </Button>

      <Dialog open={asking} onClose={() => setAsking(false)}>
        <DialogTitle>{invalidated ? 'Obnoviť objednávku?' : 'Zneplatniť objednávku?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {invalidated
              ? 'Objednávka sa vráti do zoznamov aj do štatistík.'
              : 'Objednávka zmizne zo zoznamov, z fronty aj zo štatistík. Nič sa nezmaže — obnoviť ju viete na tejto istej stránke.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setAsking(false)}>Zrušiť</MuiButton>
          <MuiButton color={invalidated ? 'primary' : 'error'} disabled={busy} onClick={() => void apply()}>
            {invalidated ? 'Obnoviť' : 'Zneplatniť'}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** Says plainly that this order is not in circulation. */
function InvalidatedBanner() {
  const record = useRecordContext<Order>();
  if (record?.invalidated !== true) return null;

  return (
    <Alert severity="error" variant="filled">
      Táto objednávka je zneplatnená — nezobrazuje sa v zoznamoch ani v štatistikách.
    </Alert>
  );
}

function Actions() {
  const record = useRecordContext<Order>();
  const notify = useNotify();
  const refresh = useRefresh();
  // Invalidating stays available whatever else is true of the order, so the
  // toolbar is not empty on a fulfilled one.
  if (record == null) return <TopToolbar />;

  const fulfil = async () => {
    try {
      await callAction(`orders/${record.id}/fulfil`);
      notify('Objednávka označená ako vybavená.', { type: 'success' });
      refresh();
    } catch {
      notify('Objednávku sa nepodarilo označiť — možno vás niekto predbehol.', { type: 'warning' });
      refresh();
    }
  };

  return (
    <TopToolbar sx={{ justifyContent: 'space-between' }}>
      <BackLink />
      <Stack direction="row" spacing={1}>
        {!record.fulfilled && (
          <Button label="Označiť ako vybavenú" onClick={fulfil}><CheckCircleIcon /></Button>
        )}
        <InvalidateButton />
      </Stack>
    </TopToolbar>
  );
}

export function OrderShow() {
  return (
    <Show actions={<Actions />} title="Objednávka">
      <OrderBody />
    </Show>
  );
}
